import { useState } from 'react';
import { Button } from '@fluentui/react-components';
import { LinkRegular } from '@fluentui/react-icons';
import SelectItemDialog from '@modules/items/components/dialogs/SelectItemDialog/SelectItemDialog';
import { AccessType } from '@modules/access/types';
import type { TSelectItemDialogState } from '@modules/items/types/dialogs.types';
import type { TItem } from '@modules/items/types/items.types';
import { SEARCH_BLOCKS_CONDITION } from '@modules/search/constants';
import { getLinkedItemKey, withoutSearchSnippet } from '../linkedItems.helper';
import TaskLinkedItemsList from './TaskLinkedItemsList';

type TProps = {
  value: TItem[];
  onChange: (value: TItem[]) => void;
  /** The Project's product, QM or countries folder, where the picker opens. */
  rootItemId: string;
};

const TaskLinkedItemsField = ({ value, onChange, rootItemId }: TProps) => {
  const [selectItemDialogState, setSelectItemDialogState] = useState<TSelectItemDialogState>();
  const linkedKeys = new Set(value.map(getLinkedItemKey));
  // Folders with partial access stay navigable but cannot be linked.
  const getDisabledReason = (item: TItem) =>
    linkedKeys.has(getLinkedItemKey(item))
      ? 'This item is already linked.'
      : item.accessRight < AccessType.READ
        ? 'Read access required.'
        : undefined;
  const openSelectItemDialog = () => {
    const dialogState: TSelectItemDialogState = {
      isOpen: true,
      onSelect: async (items) => {
        onChange([...value, ...items.map(withoutSearchSnippet)]);
        setSelectItemDialogState({ ...dialogState, isOpen: false });
      },
    };
    setSelectItemDialogState(dialogState);
  };
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <TaskLinkedItemsList
        linkedItems={value}
        onRemove={(removed) => onChange(value.filter((item) => getLinkedItemKey(item) !== getLinkedItemKey(removed)))}
      />
      <Button className="self-start" icon={<LinkRegular />} onClick={openSelectItemDialog}>
        Link items
      </Button>
      <SelectItemDialog
        initialFolderId={rootItemId}
        state={selectItemDialogState}
        setState={setSelectItemDialogState}
        title="Link items"
        actionText="Link"
        multiSelect
        navigatableFolderTypes={['NORMAL', 'COUNTRY']}
        selectableTypes={['FOLDER', 'DOCUMENT', 'FILE']}
        getDisabledReason={getDisabledReason}
        enableSearch
        searchOptions={{
          itemTypes: ['DOCUMENT', 'FILE'],
          blocksCondition: SEARCH_BLOCKS_CONDITION.NO,
          placeholder: 'Search documents and files',
        }}
      />
    </div>
  );
};
export default TaskLinkedItemsField;
